import { CheckOutlined, ClockCircleOutlined, DeleteOutlined, EyeOutlined, FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { AssignmentActionButton, ReloadButton, StyledHeadTitle } from '@lib/common';
import { Avatar, Empty, Spin, Input, Popconfirm, Space } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { TabContent, Toolbar, Search, FilterWrapper, FilterTrigger, FilterBadge, FilterPanel, FilterTitle, FilterOption, FilterCheck, MemberList, MemberCard, MemberInfo, MemberName, MemberEmail, MemberTime, MemberAction, MemberRemoveButton, LoadMore } from './class-detail.styles';
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";

var formatJoinedAt = function formatJoinedAt(value) {
  if (!value) return '?';
  var timestamp = /^\d+$/.test(value) ? Number(value) : Date.parse(value);
  return Number.isNaN(timestamp) ? '?' : new Date(timestamp).toLocaleString();
};

export var ClassMembersTab = function ClassMembersTab(_ref) {
  var labels = _ref.labels,
      joinedLabel = _ref.joinedLabel,
      actionLabels = _ref.actionLabels,
      onViewStudent = _ref.onViewStudent,
      members = _ref.members,
      _ref$profiles = _ref.profiles,
      profiles = _ref$profiles === void 0 ? [] : _ref$profiles,
      _ref$defaultRole = _ref.defaultRole,
      defaultRole = _ref$defaultRole === void 0 ? 'student' : _ref$defaultRole,
      _ref$reloadKey = _ref.reloadKey,
      reloadKey = _ref$reloadKey === void 0 ? 0 : _ref$reloadKey,
      updating = _ref.updating,
      onRemoveMember = _ref.onRemoveMember,
      onReload = _ref.onReload,
      nextTokens = _ref.nextTokens,
      loading = _ref.loading,
      counts = _ref.counts,
      onQuery = _ref.onQuery;

  var t = function t(key) {
    return labels[key.replace('labels.', '')] || key;
  };

  var _useState = useState(''),
      search = _useState[0],
      setSearch = _useState[1];

  var _useState2 = useState(defaultRole),
      role = _useState2[0],
      setRole = _useState2[1];

  var _useState3 = useState(false),
      filterOpen = _useState3[0],
      setFilterOpen = _useState3[1];

  var filteredMembers = useMemo(function () {
    return members.filter(function (member) {
      return member.role === role;
    });
  }, [members, role]);
  useEffect(function () {
    var timer = window.setTimeout(function () {
      return onQuery(role, search.trim());
    }, 250);
    return function () {
      return window.clearTimeout(timer);
    };
  }, [role, search, onQuery, reloadKey]);
  return /*#__PURE__*/_jsxs(TabContent, {
    children: [/*#__PURE__*/_jsxs(Space, {
      direction: "vertical",
      size: 20,
      style: {
        width: '100%'
      },
      children: [/*#__PURE__*/_jsxs(StyledHeadTitle, {
        children: [role === 'student' ? t('labels.students') : t('labels.teachers'), " (", counts[role], ")"]
      }), /*#__PURE__*/_jsxs(Toolbar, {
        children: [/*#__PURE__*/_jsx(Search, {
          children: /*#__PURE__*/_jsx(Input, {
            allowClear: true,
            value: search,
            placeholder: t('labels.searchMembers'),
            prefix: /*#__PURE__*/_jsx(SearchOutlined, {}),
            onChange: function onChange(event) {
              return setSearch(event.target.value);
            }
          })
        }), /*#__PURE__*/_jsxs(FilterWrapper, {
          tabIndex: -1,
          onBlur: function onBlur(event) {
            return !event.currentTarget.contains(event.relatedTarget) && setFilterOpen(false);
          },
          children: [/*#__PURE__*/_jsxs(FilterTrigger, {
            type: "button",
            onClick: function onClick() {
              return setFilterOpen(function (open) {
                return !open;
              });
            },
            "aria-haspopup": "listbox",
            "aria-expanded": filterOpen,
            children: [/*#__PURE__*/_jsx(FilterOutlined, {}), t('labels.filters'), /*#__PURE__*/_jsx(FilterBadge, {
              children: "1"
            })]
          }), filterOpen ? /*#__PURE__*/_jsxs(FilterPanel, {
            role: "listbox",
            children: [/*#__PURE__*/_jsx(FilterTitle, {
              children: t('labels.memberRole')
            }), ['student', 'teacher'].map(function (option) {
              return /*#__PURE__*/_jsxs(FilterOption, {
                type: "button",
                $active: role === option,
                onClick: function onClick() {
                  setRole(option);
                  setFilterOpen(false);
                },
                role: "option",
                "aria-selected": role === option,
                children: [option === 'student' ? t('labels.students') : t('labels.teachers'), /*#__PURE__*/_jsx(FilterCheck, {
                  $visible: role === option,
                  children: /*#__PURE__*/_jsx(CheckOutlined, {})
                })]
              }, option);
            })]
          }) : null]
        }), onReload && /*#__PURE__*/_jsx(ReloadButton, {
          height: 50,
          width: 50,
          handleReload: function handleReload() {
            return onQuery(role, search.trim());
          },
          tooltipTitle: t('labels.refresh'),
          "aria-label": t('labels.refreshMembers')
        })]
      })]
    }), loading && !filteredMembers.length ? /*#__PURE__*/_jsx(Spin, {}) : filteredMembers.length === 0 ? /*#__PURE__*/_jsx(Empty, {
      image: Empty.PRESENTED_IMAGE_SIMPLE,
      description: t('labels.noMembers')
    }) : /*#__PURE__*/_jsx(MemberList, {
      children: filteredMembers.map(function (member) {
        var _actionLabels$viewDet, _actionLabels, _actionLabels$remove, _actionLabels$cancel, _actionLabels$remove2;

        var profile = profiles.find(function (item) {
          return item.id.toString() === member.id;
        });
        var name = "".concat((profile === null || profile === void 0 ? void 0 : profile.firstName) || '', " ").concat((profile === null || profile === void 0 ? void 0 : profile.lastName) || '').trim() || member.id;
        var canRemove = Boolean(onRemoveMember && member.role === 'student');
        return /*#__PURE__*/_jsxs(MemberCard, {
          children: [/*#__PURE__*/_jsx(Avatar, {
            size: 48,
            children: name.charAt(0)
          }), /*#__PURE__*/_jsxs(MemberInfo, {
            children: [/*#__PURE__*/_jsx(MemberName, {
              title: name,
              children: name
            }), profile !== null && profile !== void 0 && profile.email ? /*#__PURE__*/_jsx(MemberEmail, {
              title: profile.email,
              children: profile.email
            }) : null, /*#__PURE__*/_jsxs(MemberTime, {
              children: [/*#__PURE__*/_jsx(ClockCircleOutlined, {}), joinedLabel(member.role === 'teacher' ? t('labels.teacher') : t('labels.student'), formatJoinedAt(member.joinedAt))]
            })]
          }), canRemove || member.role === 'student' && onViewStudent ? /*#__PURE__*/_jsxs(MemberAction, {
            children: [member.role === 'student' && onViewStudent && /*#__PURE__*/_jsx(AssignmentActionButton, {
              icon: /*#__PURE__*/_jsx(EyeOutlined, {}),
              onClick: function onClick() {
                return onViewStudent(member);
              },
              children: (_actionLabels$viewDet = actionLabels === null || actionLabels === void 0 ? void 0 : actionLabels.viewDetail) !== null && _actionLabels$viewDet !== void 0 ? _actionLabels$viewDet : t('labels.viewDetail')
            }), canRemove && /*#__PURE__*/_jsx(Popconfirm, {
              title: (_actionLabels = actionLabels === null || actionLabels === void 0 ? void 0 : actionLabels[member.role === 'student' ? 'removeStudent' : 'removeTeacher']) !== null && _actionLabels !== void 0 ? _actionLabels : t('removeStudent'),
              onConfirm: function onConfirm() {
                return onRemoveMember === null || onRemoveMember === void 0 ? void 0 : onRemoveMember(member);
              },
              okText: (_actionLabels$remove = actionLabels === null || actionLabels === void 0 ? void 0 : actionLabels.remove) !== null && _actionLabels$remove !== void 0 ? _actionLabels$remove : t('labels.remove'),
              cancelText: (_actionLabels$cancel = actionLabels === null || actionLabels === void 0 ? void 0 : actionLabels.cancel) !== null && _actionLabels$cancel !== void 0 ? _actionLabels$cancel : t('labels.cancel'),
              okButtonProps: {
                danger: true
              },
              children: /*#__PURE__*/_jsx(MemberRemoveButton, {
                danger: true,
                icon: /*#__PURE__*/_jsx(DeleteOutlined, {}),
                loading: updating,
                children: (_actionLabels$remove2 = actionLabels === null || actionLabels === void 0 ? void 0 : actionLabels.remove) !== null && _actionLabels$remove2 !== void 0 ? _actionLabels$remove2 : t('labels.remove')
              })
            })]
          }) : null]
        }, "".concat(member.role, "-").concat(member.id));
      })
    }), ' ', nextTokens[role] ? /*#__PURE__*/_jsx(LoadMore, {
      loading: loading,
      onClick: function onClick() {
        var token = nextTokens[role];
        if (token) onQuery(role, search.trim(), token);
      },
      children: t('labels.loadMore')
    }) : null]
  });
};